import React, { useEffect, useMemo } from "react";
import { useFieldArray, useWatch } from "react-hook-form";
import { TrashIcon } from "@heroicons/react/24/outline";
import classes from "./formDynamicStringTemp.module.css";

export const FormDynamicStringListTemp
 = ({
  control,
  register,
  fieldName,
  errors = {},
  disabled = false,
  value = [],
  onChange,
  required = false,
  uniqueKey = "id",
  valueKey = "value",
}) => {
  const isRHF = !!control && !!register;

  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldName,
  });

  // ✅ Correct way to get live values in RHF
  const watchedValues = useWatch({
    control,
    name: fieldName,
  });

  const values = isRHF ? watchedValues || [] : value;

  // Ensure at least one input exists
  useEffect(() => {
    if (isRHF && fields.length === 0) {
      append("");
    }
  }, [fields, append, isRHF]);

  /* =========================
     Normalize Items
  ========================== */

  const normalizedItems = useMemo(() => {
    return values.map((item, index) => {
      const isObject = typeof item === "object" && item !== null;

      const key =
        isObject && item?.[uniqueKey]
          ? item[uniqueKey]
          : fields?.[index]?.id ?? index;

      const inputValue = isObject
        ? item?.[valueKey] || ""
        : item || "";

      return {
        key,
        inputValue,
        index,
      };
    });
  }, [values, fields, uniqueKey, valueKey]);

  /* =========================
     Handlers
  ========================== */

  const handleAdd = () => {
    const lastValue =
      normalizedItems[normalizedItems.length - 1]?.inputValue;

    if (!lastValue || lastValue.trim() === "") return;

    if (isRHF) {
      append(
        typeof values[0] === "object"
          ? { [valueKey]: "" }
          : ""
      );
    } else {
      onChange([...values, ""]);
    }
  };

  const handleRemove = (index) => {
    if (values.length === 1) return;

    if (isRHF) {
      remove(index);
    } else {
      const newValues = [...values];
      newValues.splice(index, 1);
      onChange(newValues);
    }
  };

  const handleChange = (index, val) => {
    if (isRHF) return;

    const newValues = [...values];

    if (typeof newValues[index] === "object") {
      newValues[index] = {
        ...newValues[index],
        [valueKey]: val,
      };
    } else {
      newValues[index] = val;
    }

    onChange(newValues);
  };

  return (
    <div className={classes.wrapper}>
      {normalizedItems.map(({ key, inputValue, index }) => {
        const fieldError = errors?.[fieldName]?.[index];

        return (
          <div className={classes.row} key={key}>
            <div className={classes.inputBox}>
              <input
                type="text"
                placeholder=" "
                disabled={disabled}
                className={`${classes.input} ${
                  disabled ? classes.disabled : ""
                } ${fieldError ? classes.error : ""}`}
                {...(isRHF
                  ? register(`${fieldName}.${index}.${valueKey}`)
                  : {})}
                value={isRHF ? undefined : inputValue}
                onChange={
                  isRHF
                    ? undefined
                    : (e) => handleChange(index, e.target.value)
                }
              />

              <label className={classes.floatingLabel}>
                Enter text
                {required && (
                  <span className={classes.asterisk}>*</span>
                )}
              </label>

              {fieldError && (
                <div
                  className={classes.errorIcon}
                  title={fieldError.message}
                >
                  <TrashIcon className={classes.icon} />
                </div>
              )}
            </div>

            <button
              type="button"
              className={classes.deleteBtn}
              onClick={() => handleRemove(index)}
              disabled={values.length === 1 || disabled}
            >
              <TrashIcon className={classes.deleteBtn__img} />
            </button>
          </div>
        );
      })}

      <button
        type="button"
        className={classes.addBtn}
        onClick={handleAdd}
        disabled={disabled}
      >
        Add More
      </button>
    </div>
  );
};